import type { PreflightRow, AccountSummary } from '../../api/types'

interface Props {
  rows: PreflightRow[]
  accounts: AccountSummary[]
  referencePrice: number
  variant?: 'full' | 'compact'
}

export function PreflightGrid({ rows, accounts, referencePrice, variant = 'full' }: Props) {
  if (rows.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-zinc-500">
        No accounts armed. Toggle the arm switch in the Account Switcher to enable fleet trading.
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className="space-y-1.5 text-xs">
        {rows.map(r => {
          const a = accounts.find(x => x.accountId === r.accountId)
          return (
            <div key={r.accountId} className="flex items-center gap-2 text-zinc-300">
              <StatusDot status={r.status} />
              <span>{a?.platformId} · {a?.accountId}</span>
              <span className="ml-auto font-mono text-zinc-400">
                {r.status === 'skip' ? 'skipped' : `$${r.estCost.toFixed(2)}`}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  // Full variant
  return (
    <div>
      {rows.map(r => {
        const a = accounts.find(x => x.accountId === r.accountId)
        return (
          <div
            key={r.accountId}
            className="px-4 py-3 border-b border-zinc-900 last:border-b-0 flex items-center gap-3 hover:bg-zinc-900/40"
          >
            <StatusDot status={r.status} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="text-zinc-100">{a?.platformId} · {a?.accountId}</span>
                {r.reason && <span className="text-[11px] text-zinc-500">· {r.reason}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm text-zinc-200">
                {r.qty === 0 ? '—' : (Number.isInteger(r.qty) ? r.qty : r.qty.toFixed(3))}{' '}
                <span className="text-zinc-500">@ ${referencePrice.toFixed(2)}</span>
              </div>
              <div className="font-mono text-[11px] text-zinc-500">
                {r.estCost === 0 ? '—' : `$${r.estCost.toFixed(2)}`} ·{' '}
                {r.status === 'ok' ? (
                  <span className="text-emerald-400">will execute</span>
                ) : r.status === 'warn' ? (
                  <span className="text-amber-400">will execute</span>
                ) : (
                  <span className="text-rose-400">skip</span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StatusDot({ status }: { status: 'ok' | 'warn' | 'skip' }) {
  const cls =
    status === 'ok'   ? 'bg-emerald-500 shadow-[0_0_6px_#10b981aa]'
  : status === 'warn' ? 'bg-amber-500 shadow-[0_0_6px_#f59e0baa]'
  :                     'bg-rose-500 shadow-[0_0_6px_#ef4444aa]'
  return <span className={`w-[7px] h-[7px] rounded-full inline-block shrink-0 ${cls}`} />
}
